import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, Award, Briefcase, Heart } from 'lucide-react';
export function Story() {
  return <div className="flex flex-col w-full" data-id="element-297">
      {/* Founder Section */}
      <section id="story" className="py-24 bg-white overflow-hidden" data-id="element-298">
        <div className="max-w-7xl mx-auto px-6" data-id="element-299">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-300">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-301">
              <div className="aspect-[4/5] relative z-10" data-id="element-302">
                <img src="https://cdn.magicpatterns.com/uploads/7Ne571hqNffS95gnvgj9gH/Chef_Photoshoot(7).jpg" alt="Tisha Djissenou - Founder" className="w-full h-full object-cover rounded-sm grayscale hover:grayscale-0 transition-all duration-700" data-id="element-303" />
              </div>
              {/* Decorative Gold Frame */}
              <div className="absolute -top-6 -left-6 w-full h-full border-2 border-brand-gold/30 -z-0" data-id="element-304" />
              <div className="absolute -bottom-4 -right-4 bg-brand-gold p-8 z-20 hidden lg:block" data-id="element-305">
                <p className="text-white font-serif text-xl italic" data-id="element-306">
                  "Heritage is the secret ingredient in every dish we serve."
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-307">
              <h2 className="text-4xl md:text-5xl mb-8 font-serif" data-id="element-308">
                A Fusion of Heritage
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-309">
                <p data-id="element-310">
                  Founded by{' '}
                  <span className="text-brand-black font-bold" data-id="element-311">
                    Tisha Djissenou
                  </span>
                  , Versatile LLC is more than a catering company—it's a
                  celebration of cultural inclusivity and culinary innovation.
                </p>
                <p data-id="element-312">
                  Our mission is rooted in women's empowerment and the rich
                  tapestry of African heritage. We blend traditional flavors
                  with modern techniques to create dining experiences that
                  resonate with the diverse DMV community.
                </p>
                <p data-id="element-313">
                  Whether it's a grand wedding or an intimate corporate
                  gathering, we bring a level of craft and care that only a
                  woman-owned, heritage-led business can provide.
                </p>
              </div>
              <div className="mt-10 pt-10 border-t border-gray-100" data-id="element-314">
                <div className="flex items-center gap-4" data-id="element-315">
                  <div className="w-12 h-12 rounded-full bg-brand-gold/10 flex items-center justify-center text-brand-gold font-serif text-2xl" data-id="element-316">
                    T
                  </div>
                  <div data-id="element-317">
                    <p className="font-bold text-brand-black" data-id="element-318">
                      Tisha Djissenou
                    </p>
                    <p className="text-sm text-gray-500 uppercase tracking-widest" data-id="element-319">
                      Founder & Executive Chef
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Executive Summary Section */}
      <section className="py-24 bg-brand-black text-white relative overflow-hidden" data-id="element-320">
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1555244162-803834f70033?auto=format&fit=crop&q=80&w=2000')] bg-cover bg-center mix-blend-overlay" data-id="element-321" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-322">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-323">
            <h2 className="text-3xl md:text-5xl font-serif mb-8 text-brand-gold" data-id="element-324">
              Executive Summary
            </h2>
            <p className="text-lg md:text-2xl leading-relaxed text-white/90 font-serif italic" data-id="element-325">
              "Versatile LLC is an African-woman-owned catering business
              dedicated to delivering high-quality, diverse, and customized
              culinary experiences."
            </p>
            <div className="w-24 h-[1px] bg-brand-gold mx-auto my-8" data-id="element-326" />
            <p className="text-base md:text-lg text-gray-300 leading-relaxed font-sans" data-id="element-327">
              Our mission is to provide exceptional food and service that cater
              to various cultural backgrounds, dietary needs, and occasions. By
              focusing on inclusivity, innovation, and outstanding customer
              service, we aim to prove ourselves as a premier catering service
              provider for corporate events, weddings, social gatherings,
              community functions, and meal plans.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Business Description / At a Glance */}
      <section className="py-24 bg-gray-50" data-id="element-328">
        <div className="max-w-7xl mx-auto px-6" data-id="element-329">
          <div className="text-center mb-16" data-id="element-330">
            <h2 className="text-3xl md:text-5xl font-serif mb-4 text-brand-black" data-id="element-331">
              Versatile LLC At a Glance
            </h2>
            <p className="text-gray-500 text-lg" data-id="element-332">
              The core pillars of our business operations and identity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-333">
            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-sm shadow-sm border border-gray-100" data-id="element-334">
              <div className="w-12 h-12 bg-brand-gold/10 text-brand-gold rounded-full flex items-center justify-center mb-6" data-id="element-335">
                <Heart size={24} data-id="element-336" />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3" data-id="element-337">Ownership</h3>
              <p className="text-gray-600" data-id="element-338">
                Proudly{' '}
                <span className="font-bold text-brand-black" data-id="element-339">
                  African-Woman-Owned
                </span>
                , bringing authentic heritage and female empowerment to the
                forefront of the culinary industry.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-sm shadow-sm border border-gray-100" data-id="element-340">
              <div className="w-12 h-12 bg-brand-gold/10 text-brand-gold rounded-full flex items-center justify-center mb-6" data-id="element-341">
                <MapPin size={24} data-id="element-342" />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3" data-id="element-343">Location</h3>
              <p className="text-gray-600" data-id="element-344">
                Based in{' '}
                <span className="font-bold text-brand-black" data-id="element-345">
                  Gaithersburg, MD
                </span>
                , strategically positioned to serve the entire Washington D.C.,
                Maryland, and Northern Virginia (DMV) area.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-sm shadow-sm border border-gray-100" data-id="element-346">
              <div className="w-12 h-12 bg-brand-gold/10 text-brand-gold rounded-full flex items-center justify-center mb-6" data-id="element-347">
                <Briefcase size={24} data-id="element-348" />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3" data-id="element-349">
                Business Type
              </h3>
              <p className="text-gray-600" data-id="element-350">
                Comprehensive{' '}
                <span className="font-bold text-brand-black" data-id="element-351">
                  Catering Services
                </span>
                , ranging from full-scale event production to convenient
                drop-off and meal prep solutions.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-white p-8 rounded-sm shadow-sm border border-gray-100 lg:col-span-2" data-id="element-352">
              <div className="w-12 h-12 bg-brand-gold/10 text-brand-gold rounded-full flex items-center justify-center mb-6" data-id="element-353">
                <Users size={24} data-id="element-354" />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3" data-id="element-355">
                Target Market
              </h3>
              <p className="text-gray-600" data-id="element-356">
                We cater to corporate events, weddings, private parties, social
                gatherings, and community events. Additionally, we provide
                tailored meal plans for individuals who do not have the luxury
                to prepare healthy meals due to their busy schedules.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-brand-black text-white p-8 rounded-sm shadow-xl border border-brand-gold/20 lg:col-span-1" data-id="element-357">
              <div className="w-12 h-12 bg-brand-gold text-white rounded-full flex items-center justify-center mb-6" data-id="element-358">
                <Award size={24} data-id="element-359" />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3 text-brand-gold" data-id="element-360">
                Unique Selling Proposition
              </h3>
              <p className="text-gray-300" data-id="element-361">
                A unique fusion of diverse cuisines, customized menu options, a
                deep focus on cultural inclusivity, and the active empowerment
                of women in business.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}